import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { Badge } from "@nosible/shared/components/ui/badge";
import { useSession, authClient } from "@/admin/lib/auth-client";
import { trpc } from "@/admin/lib/trpc";
import {
  ArrowLeft,
  Building2,
  User,
  CreditCard,
  ChevronRight,
  ArrowUpRight,
} from "lucide-react";
import { SubApiKeys } from "@/admin/components/SubApiKeys";
import { SubNameDesc } from "@/admin/components/subscriptions/SubNameDesc";
import { AdminSubCurrentUsage } from "@/admin/components/subscriptions/AdminSubCurrentUsage";
import { SubTotalUsage } from "@/admin/components/subscriptions/SubTotalUsage";
import { CancelSubscription } from "@/admin/components/CancelSubscription";
import { SubscriptionHistoryDialog } from "@/admin/components/SubscriptionHistoryDialog";
import { RateLimits } from "@/admin/components/RateLimits";

export function AdminSubscriptionPage() {
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { data: session, isPending: sessionPending } = useSession();

  const {
    data: subscription,
    isLoading,
    error,
    refetch,
  } = trpc.subscription.getSubscription.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!session && !!subscriptionId }
  );

  const updateSubscriptionQueryLimitsMutation = trpc.admin.updateSubscriptionQueryLimits.useMutation();

  useEffect(() => {
    if (!session && !sessionPending) {
      navigate("/login");
    }
  }, [session, sessionPending, navigate]);

  if (sessionPending || isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-red-400">{error.message}</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-chromium-mist">Subscription not found</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <CreditCard className="h-6 w-6 text-neon-green" />
          <h1 className="text-2xl font-bold text-silica font-display">Subscription (Admin View)</h1>
        </div>
      </div>

      <div className="grid gap-6">
        {/* Owner Info */}
        <Card>
          <CardHeader>
            <div className="admin-card-heading">
              <div>
                <CardTitle>Subscription Owner</CardTitle>
                <CardDescription>Who this subscription belongs to</CardDescription>
              </div>
              {subscription.owner && (
                <Button variant="outline" size="sm" asChild>
                  {subscription.owner.type === "organization" && subscription.owner.slug ? (
                    <Link to={`/organizations/${subscription.owner.slug}`}>
                      View Organization
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  ) : subscription.owner.type === "user" ? (
                    <Link to={`/users/${subscription.owner.id}`}>
                      View User
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  ) : null}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              {subscription.owner?.type === "organization" ? (
                <div className="h-10 w-10 rounded-lg bg-slate-core flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-chromium-mist" />
                </div>
              ) : (
                <div className="h-10 w-10 rounded-lg bg-slate-core flex items-center justify-center">
                  <User className="h-5 w-5 text-chromium-mist" />
                </div>
              )}
              <div>
                <p className="font-medium text-silica">{subscription.owner?.name || "Unknown"}</p>
                <Badge variant="outline" className="mt-1">
                  {subscription.owner?.type === "organization" ? "Organization" : "Personal"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Details */}
        <Card>
          <CardHeader>
            <div className="admin-card-heading">
              <div>
                <CardTitle>Plan Details</CardTitle>
                <CardDescription>Current subscription plan and status</CardDescription>
              </div>
              <div className="admin-card-actions">
                {(subscription.status === "active" || subscription.status === "trialing") && (
                  <>
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/subscriptions/${subscriptionId}/adminUpgrade`}>
                        <ArrowUpRight className="h-4 w-4 mr-1" />
                        Admin Upgrade
                      </Link>
                    </Button>
                    <CancelSubscription
                      subscriptionId={subscriptionId!}
                      onSuccess={() => window.location.reload()}
                    />
                  </>
                )}
                <SubscriptionHistoryDialog
                  subscriptionId={subscriptionId!}
                  subscriptionName={subscription.plan.name}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-chromium-mist">Plan</p>
                <p className="text-silica font-medium capitalize">
                  {subscription.plan?.name}
                </p>
              </div>
              <div>
                <p className="text-sm text-chromium-mist">Status</p>
                <Badge
                  variant={
                    subscription.status === "active" ? "default" : "secondary"
                  }
                >
                  {subscription.status}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-chromium-mist">Period End</p>
                <p className="text-silica font-medium">
                  {subscription.periodEnd
                    ? new Date(subscription.periodEnd).toLocaleDateString()
                    : "Indefinite"}
                </p>
              </div>
              <div>
                <p className="text-sm text-chromium-mist">Subscription ID</p>
                <p className="text-silica font-mono text-xs">
                  {subscription.id}
                </p>
              </div>
              <div>
                <p className="text-sm text-chromium-mist">Plan ID</p>
                <Link
                  to={`/plans/${subscription.plan.id}`}
                  className="text-neon-green hover:underline font-mono text-xs"
                >
                  {subscription.plan.id}
                </Link>
              </div>
            </div>
            {subscription.status === "canceled" && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">
                  This subscription has been canceled. API keys have been deactivated.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription Identity */}
        <SubNameDesc
          subscriptionId={subscriptionId!}
          currentName={subscription.name}
          currentDescription={subscription.description ?? null}
          onSuccess={refetch}
        />

        {/* Rate Limits */}
        <RateLimits
          title="Subscription Rate Limits"
          description="Custom rate limits for this subscription (overrides plan defaults)"
          queryLimits={subscription.queryLimits || []}
          onSave={async (limits) => {
            await updateSubscriptionQueryLimitsMutation.mutateAsync({
              subscriptionId: subscriptionId!,
              queryLimits: limits,
            });
            await refetch();
          }}
          isLoading={updateSubscriptionQueryLimitsMutation.isPending}
        />

        {/* API Keys */}
        <SubApiKeys subscriptionId={subscriptionId!} />

        {/* Usage */}
        <AdminSubCurrentUsage subscriptionId={subscriptionId!} />

        {/* Total Usage (All Time) */}
        <SubTotalUsage subscriptionId={subscriptionId!} />

      </div>
    </div>
  );
}
